'use client';

import { useGetInformation } from 'Shared/hooks/api';
import { useTokenStore } from 'Shared/hooks/useTokenStore';
import { Analytics, createReturnUrl } from 'Shared/utils/client';
import { LINK } from '@constants';
import {
  ArrowRight,
  Box,
  Button,
  Column,
  getColorValue,
  Icon,
  Row,
  Tab,
  Typography,
  useTheme,
  ViewingMenu,
} from '@viewup/ui';
import { SubHeaderLayout } from 'app/_components';
import { WithGtmDataLayer } from 'app/_components/WithGtmDataLayer';
import { HideBottomBarHoc, useGlobalModalStoreBase } from 'app/_hook';
import { ViewingPlatform } from 'app/viewing/_entities/common';
import {
  isSuspendedPredicate,
  useGetActiveNBlogAccount,
  useGetPenaltyInfo,
  useNBlogAccountsStatus,
} from 'app/viewing/_entities/post';
import { NblogAccountManage } from 'app/viewing/_modules/post/NBlogAccountManage';
import { OverviewPostCampaignList } from 'app/viewing/_modules/post/OverviewPostCampaignList';
import { PostCampaignList } from 'app/viewing/_modules/post/PostCampaignList';
import { PrivatePostCampaignList } from 'app/viewing/_modules/post/PrivatePostCampaignList/PrivatePostCampaignList';
import { SuspensionAlertModal } from 'app/viewing/_modules/post/SuspensionAlertModal';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import type { MouseEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
export const PostViewingClientPage = HideBottomBarHoc(
  WithGtmDataLayer(() => {
    const router = useRouter();
    const theme = useTheme();
    const { setConfirmModal, setAlertModal } = useGlobalModalStoreBase((state) => state.actions);

    const { accessToken } = useTokenStore();
    const { data: activeAccount } = useGetActiveNBlogAccount();
    const { data: penaltyInfo } = useGetPenaltyInfo(activeAccount?.accountUuid || '');
    const { data: accountStatus } = useNBlogAccountsStatus();
    const { data: userInfo } = useGetInformation();

    const [isOpenSuspensionAlert, setIsOpenSuspensionAlert] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const tab = useSearchParams().get('type');
    const tabRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // 신청하기, 신청현황 탭 스티키 처리
    useEffect(() => {
      if (!tabRef) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsSticky(!entry.isIntersecting);
        },
        { rootMargin: '-89px', threshold: 0 },
      );

      observerRef.current = observer;

      const sentinel = document.createElement('div');

      if (tabRef?.current) {
        sentinel.style.top = '0';
        sentinel.style.height = '1px';
        sentinel.style.width = '100%';

        tabRef?.current?.parentNode?.insertBefore(sentinel, tabRef.current);
        observer.observe(sentinel);
      }

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          sentinel.remove();
        }
      };
    }, [tabRef.current]);

    // 클릭 이벤트 캡처링 제어
    const handleRootClickCapture: MouseEventHandler<HTMLElement> = (e) => {
      const buttonElement = (e.target as Element).closest('button') as HTMLButtonElement | null;
      if (!buttonElement) return;

      const buttonClassName = buttonElement.className;

      // 로그인 여부
      if (!checkLoggedIn()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // 블로그 계정 여부 검사
      const targetClassNames1 = ['applyButton', 'moveToHistoriesButton'];
      const hasBlogAccount = checkHasBlogAccount(targetClassNames1, buttonClassName);
      if (!hasBlogAccount) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // 계정 상태 검사
      const targetClassNames2 = ['applyButton', 'submitButton'];
      const hasValidAccountStatus = checkAccountStatus(targetClassNames2, buttonClassName);
      if (!hasValidAccountStatus) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    };

    /**
     * 로그인 상태 확인 및 미로그인 시 로그인 모달 표시
     */
    const checkLoggedIn = () => {
      if (!accessToken) {
        Analytics.getInstance().track('NONLOG_VIEWING_MAIN_MENU', {
          viewingType: 'posting',
        });
        setConfirmModal({
          content: `본 서비스는 회원만 이용이 가능해요.\n로그인할까요?`,
          onClickFillButton: () => {
            router.push(`${LINK.LOGIN}${createReturnUrl(LINK.VIEWING_POST_APPLY_LIST, false)}`);
          },
        });
        return false;
      }
      return true;
    };

    /**
     * 블로그 계정 유무 확인 및 없을 경우 알림 표시
     * @param targetClassNames 확인할 버튼 클래스명 목록
     * @param buttonClassName 현재 클릭된 버튼의 클래스명
     */
    const checkHasBlogAccount = (classNames: string[], targetClassName: string) => {
      if (!accessToken) return;

      if (
        accountStatus?.hasNoAccounts &&
        classNames.some((name) => targetClassName?.includes(name))
      ) {
        setAlertModal({
          actionButtonText: '계정 추가하러 가기',
          content: '추가된 계정이 없어요.',
          onClickActionButton: () => {
            router.push(`${LINK.ACCOUNT_ADD}?platform=${ViewingPlatform.NBLOG}`);
          },
        });

        return false;
      }

      return true;
    };

    /**
     * 계정 상태 확인 (에러, 승인, 패널티)
     * @param targetClassNames 확인할 버튼 클래스명 목록
     * @param buttonClassName 현재 클릭된 버튼의 클래스명
     */
    const checkAccountStatus = (classNames: string[], targetClassName: string) => {
      if (!accessToken) return;

      if (classNames.some((name) => targetClassName.includes(name))) {
        if (!activeAccount) {
          setAlertModal({
            actionButtonText: '계정관리 바로가기',
            content: '연결된 네이버블로그 계정이 없습니다.',
            onClickActionButton: () => {
              router.push(`${LINK.MY_VIEWUP_ACCOUNT}`);
            },
          });
          return false;
        }

        if (penaltyInfo?.isSuspended) {
          setIsOpenSuspensionAlert(true);
          return false;
        }
      }

      return true;
    };

    return (
      <SubHeaderLayout
        headerLayout={{
          rightContent: (
            <Link href={LINK.VIEWING}>
              <ViewingMenu />
            </Link>
          ),
        }}
        title='포스팅 뷰업'
      >
        <Column onClickCapture={handleRootClickCapture} width={'100%'}>
          <Column gap={16} p={16}>
            <Row alignItems='center' gap={4} justifyContent='space-between'>
              <Typography color='text.primary' fontType='body.medium'>
                {accountStatus?.hasNoAccounts || !accessToken
                  ? '활동을 시작해 볼까요?👀'
                  : `${userInfo?.data.id || ''} 님`}
              </Typography>

              <Button
                className='moveToHistoriesButton'
                onClick={() => router.push(LINK.VIEWING_POST_HISTORY)}
                rightContent={<Icon source={ArrowRight} />}
                size='S'
                variant='ghost.assistive'
              >
                내 참여 기록
              </Button>
            </Row>

            <NblogAccountManage />
          </Column>

          {/* 포스팅 리스트 탭 */}
          {
            <Box data-tour='post-tab' ref={tabRef}>
              <Tab
                activeStyle={{ ...theme.typography.body2.medium }}
                defaultValue='apply'
                onChange={(value) => {
                  window.history.replaceState(null, '', `?type=${value}`);
                }}
                size='L'
                styleComponents={{
                  root: {
                    css: {
                      '[role="tabpanel"]': {
                        height: '100%',
                      },
                      '> div': {
                        '> div': {
                          overflow: 'unset',
                        },
                        height: '100%',
                      },
                    },
                    height: '100%',
                  },
                  trigger: {
                    fontType: 'body2.regular',
                    mx: 16,
                  },
                  wrapper: {
                    css: {
                      borderBottom: `1px solid ${getColorValue('border.tertiary', theme)}`,
                      ...(isSticky && {
                        backgroundColor: 'white',
                        height: 51,
                        position: 'absolute',
                        top: 40,
                        width: '100%',
                        zIndex: theme.zIndex.fixed[1000],
                      }),
                    },
                  },
                }}
                tabItem={[
                  {
                    content: (
                      <Column
                        bgColor='bg.tertiary'
                        css={{ minHeight: 'calc(100svh - 304px)' }}
                        height={'100%'}
                        px={16}
                        py={24}
                      >
                        {accessToken ? <PostCampaignList /> : <PrivatePostCampaignList />}
                      </Column>
                    ),
                    label: '신청하기',
                    value: 'apply',
                  },
                  {
                    content: (
                      <Column
                        bgColor='bg.tertiary'
                        css={{ minHeight: 'calc(100svh - 304px)' }}
                        height={'100%'}
                        px={16}
                        py={24}
                      >
                        {accessToken ? <OverviewPostCampaignList /> : <PrivatePostCampaignList />}
                      </Column>
                    ),
                    label: '신청현황',
                    value: 'overview',
                  },
                ]}
                value={tab ? tab : 'apply'}
                variant='line'
              />
            </Box>
          }
        </Column>

        {isOpenSuspensionAlert && penaltyInfo && isSuspendedPredicate(penaltyInfo) && (
          <SuspensionAlertModal
            {...penaltyInfo}
            exitHandler={() => setIsOpenSuspensionAlert(false)}
            isOpen
          />
        )}
      </SubHeaderLayout>
    );
  }),
);
